/**
 * Client portal (exporter logins). Admins create the logins; a client sees only the requests their company
 * raised in the portal and, once BD has approved the compiled dossier, the files delivered on them.
 */
import type { ClientRequestType } from './dossier';
import type { FileRefDto } from './dto';

/** What a client sees instead of the internal request / BD review states. */
export const CLIENT_STAGES = ['received', 'in_preparation', 'final_review', 'completed', 'cancelled'] as const;
export type ClientStage = (typeof CLIENT_STAGES)[number];

export const CLIENT_STAGE_LABELS: Record<ClientStage, string> = {
  received: 'Received',
  in_preparation: 'In process',
  final_review: 'Under RA review',
  completed: 'Ready to download',
  cancelled: 'Cancelled',
};

/** `ready` holds every dossier with a released version, including one reopened for the next version. */
export type ClientRequestView = 'open' | 'ready' | 'cancelled' | 'all';

export interface ClientRequestCountsDto {
  open: number;
  ready: number;
  cancelled: number;
  all: number;
}

export interface ClientOptionsDto {
  company: { id: string; name: string };
  countries: string[];
  categories: string[];
  dosageForms: string[];
  documents: string[];
}

export interface ClientRequestSummaryDto {
  id: string;
  code: string;
  /** The brand name, or the generic name when the product has no brand. */
  productName: string;
  /** Null for requisitions from before the portal asked for them. */
  genericName: string | null;
  brandName: string | null;
  /** A product like this one that the client already has. */
  similarProduct: string | null;
  country: string;
  category: string | null;
  dosageForm: string | null;
  packSize: string | null;
  requestType: ClientRequestType | null;
  /** The date Makin gives the client (set by RA), `YYYY-MM-DD`; null until Makin sets one. */
  requiredBy: string | null;
  stage: ClientStage;
  /** Documents on the checklist that are ready, out of all on it (or prepared items when there is no checklist). */
  progress: { ready: number; total: number };
  raisedBy: string;
  /** How many versions Makin has released (a reopened request keeps its earlier ones). */
  releasedVersions: number;
  /** Makin's clarifications still waiting for the client's answer. */
  openClarifications: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
}

export type ClientDocumentStatus = 'pending' | 'in_preparation' | 'ready';

export interface ClientDocumentDto {
  key: string;
  name: string;
  required: boolean;
  notes: string | null;
  status: ClientDocumentStatus;
}

export interface ClientTimelineEntryDto {
  at: string;
  label: string;
  detail: string | null;
  /** Documents the entry is about, e.g. the ones a change request names. */
  documents?: string[];
  /** Files the client can open from the entry: their change-request attachments, a released version, reference files. */
  files?: FileRefDto[];
}

/** A version of the documents Makin released to the client. */
export interface ClientReleaseDto {
  version: number;
  files: FileRefDto[];
  link: { url: string; label: string | null } | null;
  releasedAt: string;
  /** What the client asked to change, which this version answers (none for the first). */
  changeNote: string | null;
}

/** A query the client raised on the released dossier, and RA's compliance once it is sent. */
export interface ClientChangeRequestDto {
  id: string;
  /** Query 1, 2, …, in the order they were raised. */
  number: number;
  /** Still waiting for Makin's compliance. */
  open: boolean;
  afterVersion: number;
  comment: string;
  documents: string[];
  files: FileRefDto[];
  requestedAt: string;
  requestedBy: string;
  /** Makin's reply and corrected documents. */
  compliance: { comment: string; files: FileRefDto[]; sentAt: string } | null;
  /** The version that answered it (queries answered before compliance existed). */
  resolvedVersion: number | null;
}

/** The corrected documents Makin sent as the compliance on one of the client's queries. */
export interface ClientComplianceFilesDto {
  /** Query 1, 2, … */
  query: number;
  files: FileRefDto[];
  sentAt: string;
}

/** Makin asked the client to explain their requisition (or a query) more clearly, or to send reference documents. */
export interface ClientClarificationDto {
  id: string;
  number: number;
  /** The query it is about (Query 1, 2, …); null for the requisition itself. */
  query: number | null;
  question: string;
  files: FileRefDto[];
  askedAt: string;
  answer: { comment: string; files: FileRefDto[]; answeredAt: string; answeredBy: string } | null;
  open: boolean;
}

/** A dossier with the versions Makin released and its compliance files, for the client's Ready to download page. */
export interface ClientReleasedDossierDto {
  id: string;
  code: string;
  productName: string;
  country: string;
  stage: ClientStage;
  /** Newest first. */
  releases: ClientReleaseDto[];
  /** Compliance with files, newest first. */
  compliances: ClientComplianceFilesDto[];
}

export interface ClientRequestDetailDto extends ClientRequestSummaryDto {
  notes: string | null;
  documents: ClientDocumentDto[];
  referenceFiles: FileRefDto[];
  /** Every version Makin released, newest first. Internal drafts never appear here. */
  releases: ClientReleaseDto[];
  /** Queries, newest first. */
  changeRequests: ClientChangeRequestDto[];
  /** Makin's clarifications, newest first; open ones wait for the client's answer. */
  clarifications: ClientClarificationDto[];
  /** The client can answer Makin's open clarifications (not on a cancelled request). */
  canAnswerClarifications: boolean;
  cancelReason: string | null;
  cancelledByClient: boolean;
  canCancel: boolean;
  canAddFiles: boolean;
  /** Once released, the client can raise queries (several can be open); each reopens the request until answered. */
  canRequestChanges: boolean;
  timeline: ClientTimelineEntryDto[];
}
